import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type {
  MasterListParams,
  MasterListResponse,
  MasterItemResponse,
} from '@/features/master/types';

/**
 * 企業マスタのドメイン型。
 * admin-console-api の詳細実装がまだのため暫定フィールド。
 */
export interface Company {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

const BASE = '/master/companies';

/**
 * 企業マスタ用 queryOptions factory。
 * `useQuery` / `ensureQueryData` / `prefetchQuery` で使い回す。
 */
export const companyQueries = {
  all: ['companies'] as const,
  list: (params: MasterListParams) =>
    queryOptions({
      queryKey: ['companies', 'list', params] as const,
      queryFn: async () => {
        const { data } = await apiClient.get<MasterListResponse<Company>>(BASE, {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          query: params.query,
        });
        return data;
      },
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: ['companies', 'detail', id] as const,
      queryFn: async () => {
        const { data } = await apiClient.get<MasterItemResponse<Company>>(`${BASE}/${id}`);
        return data;
      },
    }),
};

/**
 * 企業作成。
 */
export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<Company, 'id'>) => {
      const { data } = await apiClient.post<MasterItemResponse<Company>>(BASE, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyQueries.all });
    },
  });
}

/**
 * 企業更新。
 */
export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Company) => {
      const { data } = await apiClient.put<MasterItemResponse<Company>>(
        `${BASE}/${id}`,
        input
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: companyQueries.all });
      qc.invalidateQueries({ queryKey: ['companies', 'detail', vars.id] });
    },
  });
}

/**
 * 企業削除。
 */
export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${BASE}/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyQueries.all });
    },
  });
}
